import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ChequePrintData } from '@/api/cheque.api';

/**
 * Renders cheque stationery — the fields, at the millimetre coordinates the layout gives, on
 * paper the size the layout gives (GAP-006, GAP-007).
 *
 * Shared by the single-cheque print on the cheque form and the batch run on the print queue, so a
 * cheque comes out the same whether it was printed alone or with nine others. Everything here is
 * driven by the layout the server merged in; nothing about any bank's stationery is known to this
 * file.
 */

interface SheetProps {
  data: ChequePrintData;
}

/** One page. Positioned relative to its own box so N of them can stack as N pages. */
export const ChequePrintSheet = ({ data }: SheetProps) => (
  <div
    className="cheque-print-page"
    style={{
      position:   'relative',
      width:      `${data.pageWidthMm}mm`,
      height:     `${data.pageHeightMm}mm`,
      fontFamily: data.defaultFontName || 'Arial, sans-serif',
      fontSize:   `${data.defaultFontSizePt}pt`,
      overflow:   'hidden',
      // Each cheque is its own sheet of stationery. Without this the second cheque prints on the
      // bottom half of the first one's page.
      breakAfter: 'page',
      pageBreakAfter: 'always',
    }}
  >
    {/* Payee name */}
    <span style={{
      position: 'absolute',
      left:     `${data.payeeNameX}mm`,
      top:      `${data.payeeNameY}mm`,
      width:    `${data.payeeNameWidth}mm`,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    }}>
      {data.payeeName}
    </span>

    {/* Amount in figures */}
    <span style={{
      position: 'absolute',
      left:     `${data.amountFiguresX}mm`,
      top:      `${data.amountFiguresY}mm`,
      width:    `${data.amountFiguresWidth}mm`,
      fontSize: `${data.amountFontSizePt}pt`,
      textAlign: 'right',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    }}>
      {data.currencyCode} {data.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>

    {/* Amount in words */}
    <span style={{
      position: 'absolute',
      left:     `${data.amountWordsX}mm`,
      top:      `${data.amountWordsY}mm`,
      width:    `${data.amountWordsWidth}mm`,
      overflow: 'hidden',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    }}>
      {data.amountInWords}
    </span>

    {/* Cheque date */}
    <span style={{
      position: 'absolute',
      left:     `${data.chequeDateX}mm`,
      top:      `${data.chequeDateY}mm`,
      whiteSpace: 'nowrap',
    }}>
      {data.chequeDate}
    </span>

    {/* Cheque number */}
    <span style={{
      position: 'absolute',
      left:     `${data.chequeNoX}mm`,
      top:      `${data.chequeNoY}mm`,
      whiteSpace: 'nowrap',
    }}>
      {data.chequeNumber}
    </span>

    {/*
      Crossing (GAP-005 / GAP-006). The server sends text only for a cheque that is actually
      crossed — an open cheque and one whose crossing was never stated both arrive as null, and
      printing "A/C PAYEE ONLY" on either would assert a restriction the cheque does not carry.
      Coordinates come with it; without them there is nowhere to put the words.
    */}
    {data.crossingText && data.crossingTextX != null && data.crossingTextY != null && (
      <span style={{
        position: 'absolute',
        left:     `${data.crossingTextX}mm`,
        top:      `${data.crossingTextY}mm`,
        whiteSpace: 'nowrap',
        fontWeight: 600,
        letterSpacing: '0.05em',
      }}>
        {data.crossingText}
      </span>
    )}

    {/*
      Rubber stamp (GAP-007) — the company's authority block. Null when the layout has none or the
      operator turned it off for this run because the stationery is stamped by hand; either way
      nothing prints.
    */}
    {data.rubberStampText && data.rubberStampX != null && data.rubberStampY != null && (
      <span style={{
        position: 'absolute',
        left:     `${data.rubberStampX}mm`,
        top:      `${data.rubberStampY}mm`,
        whiteSpace: 'nowrap',
        fontWeight: 600,
      }}>
        {data.rubberStampText}
      </span>
    )}
  </div>
);

interface LayerProps {
  /** One entry per sheet of stationery. A single-cheque print passes an array of one. */
  cheques: ChequePrintData[];
}

/** The id the print CSS keys on. An id, not a class, for the specificity reason below. */
const PRINT_ROOT_ID = 'cheque-print-root';

/**
 * The print layer: hidden on screen, and on print it is the only thing on the page.
 *
 * <b>Rendered through a portal into document.body, and that is not a detail.</b> Isolating a print
 * region means hiding body's other children — but React mounts the whole app inside #root, so a
 * layer rendered in place is a DESCENDANT of #root. Hiding "every body child that is not the
 * layer" then hides #root, which contains the layer, and no amount of `display: block` on a
 * descendant brings back a subtree whose ancestor is display:none. The page prints blank. A portal
 * makes the layer a real sibling of #root, so the rule means what it says.
 *
 * The selectors are prefixed `html body >` on purpose. index.css already carries a global print
 * rule — `body > *:not(#root) { display: none !important }` — for the report screens, and it would
 * otherwise hide this portal: with both marked !important the more specific selector wins, and an
 * id inside :not() outranks a class. Matching on an id and adding `html` puts this above it
 * without depending on stylesheet order.
 *
 * Page size comes from the first cheque. A run mixing two stationery sizes cannot be expressed in
 * one @page rule — a real constraint of printing, not of this code: you would be feeding two
 * different stacks of paper into one printer anyway. It is declared last so it beats the A4
 * landscape default index.css sets for reports.
 */
export const ChequePrintLayer = ({ cheques }: LayerProps) => {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.id = PRINT_ROOT_ID;
    document.body.appendChild(el);
    setHost(el);
    return () => { el.remove(); };
  }, []);

  if (cheques.length === 0 || !host) return null;
  const [first] = cheques;

  return createPortal(
    <>
      <style>{`
        @media print {
          html body > *:not(#${PRINT_ROOT_ID}) { display: none !important; }
          html body > #${PRINT_ROOT_ID} { display: block !important; }
          .cheque-print-page:last-child { break-after: auto; page-break-after: auto; }
          @page { size: ${first.pageWidthMm}mm ${first.pageHeightMm}mm; margin: 0; }
        }
        html body > #${PRINT_ROOT_ID} { display: none; }
      `}</style>
      <div className="cheque-print-layer">
        {cheques.map((c, i) => (
          <ChequePrintSheet key={`${c.chequeNumber}-${i}`} data={c} />
        ))}
      </div>
    </>,
    host);
};
